import {
  normalizeComplaint,
  toComplaintText,
} from 'features/Schedule/api/chiefComplaints.transform'
import type {
  ChiefComplaintRow,
  ChiefComplaintSet,
} from 'features/Schedule/api/chiefComplaints.types'

import type { ComplaintEntry } from './complaintField.types'

type Entries = readonly ComplaintEntry[]
type Rows = readonly ChiefComplaintRow[]

const DRAFT_KEY = 'draft'

export const seedEntries = (rows: Rows): ComplaintEntry[] =>
  rows.map((row) => ({ key: `saved-${row.id}`, id: row.id, complaint: row.complaint }))

export const hasComplaint = (list: Entries, text: string, exceptKey?: string): boolean =>
  list.some(
    (entry) =>
      entry.key !== exceptKey && normalizeComplaint(entry.complaint) === normalizeComplaint(text),
  )

export const addComplaint = (list: Entries, text: string, key: string): Entries => {
  const complaint = toComplaintText(text)
  if (!complaint || hasComplaint(list, complaint)) return list
  return [...list, { key, id: 0, complaint }]
}

// Blank text or another row's words leave the row as it was.
export const rewordComplaint = (list: Entries, key: string, text: string): Entries => {
  const complaint = toComplaintText(text)
  if (!complaint || hasComplaint(list, complaint, key)) return list
  return list.map((entry) => (entry.key === key ? { ...entry, complaint } : entry))
}

// Words still in the box count at save time, as OpenEMR's ccFlush keeps them.
export const applyDraft = (list: Entries, draft: string, rewordKey: string | null): Entries =>
  rewordKey ? rewordComplaint(list, rewordKey, draft) : addComplaint(list, draft, DRAFT_KEY)

export const removeComplaint = (list: Entries, key: string): Entries =>
  list.filter((entry) => entry.key !== key)

export const isComplaintListDirty = (list: Entries, seed: Rows): boolean =>
  list.length !== seed.length ||
  list.some(
    (entry, index) => entry.id !== seed[index].id || entry.complaint !== seed[index].complaint,
  )

export const toReplaceRows = (list: Entries): { id: number; complaint: string }[] =>
  list.map(({ id, complaint }) => ({ id, complaint }))

const sameWords = (list: Entries, rows: Rows): boolean =>
  list.length === rows.length &&
  list.every((entry, index) => entry.complaint === rows[index].complaint)

// Server data wins when frozen, when nothing was edited, or when it is the saved form of the edits.
export const mergeServerRows = (list: Entries, seed: Rows, server: ChiefComplaintSet): Entries => {
  const fresh = seedEntries(server.complaints)
  if (server.frozen || !isComplaintListDirty(list, seed)) return fresh
  if (!sameWords(list, server.complaints)) return list
  return list.map((entry, index) => ({ ...entry, id: server.complaints[index].id }))
}
